import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { driverRecordShape } from './driverRecordShape';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';

// ETA risk forecast list rendered under the route map. Pulls each pending
// routeStop's forecast fields (originalEtaLabel, currentEtaLabel, riskTier,
// riskReason — populated by composeDriverRecords) and flattens them across
// visible drivers. Green-tier stops are intentionally hidden so the list
// stays a "what's at risk right now" surface; the chip totals only yellow
// and red. A per-row "Closes X PM" building-closure pill returns when the
// dedicated BE closure query lands (stop.closesAtMs — see the forecast
// header comment in composeDriverRecords).
//
// See `docs/development/live-route-monitoring-design.md` (Forecast model
// sub-section) and `docs/mocks/009-day-of-realtime-monitoring-mock.html`
// (`.map-forecast` block) for the source-of-truth spec.

const TIER_ORDER = { red: 0, yellow: 1, green: 2 };

const StyledDiv = styledComponent('div', {
  borderTop: `1px solid ${theme.colors.borderLight}`,
  background: theme.colors.bgCard,
  maxHeight: '240px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',

  '& .rfl-header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    background: theme.colors.bgCard,
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  '& .rfl-title': {
    fontSize: theme.sizes.smallFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
    letterSpacing: '-0.005em',
  },
  '& .rfl-title-meta': {
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textMuted,
    fontWeight: theme.fontWeights.normal,
    marginLeft: '6px',
  },
  '& .rfl-summary-chip': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '3px 9px',
    borderRadius: theme.radii.badge,
    fontSize: theme.sizes.captionFontSize,
    fontWeight: theme.fontWeights.semibold,
    background: theme.colors.statusOffTrackBg,
    color: theme.colors.statusOffTrack,
    border: `1px solid ${theme.colors.statusOffTrack}40`,
    whiteSpace: 'nowrap',
  },
  '& .rfl-summary-chip.zero': {
    background: theme.colors.statusOnTrackBg,
    color: theme.colors.statusOnTrack,
    borderColor: `${theme.colors.statusOnTrack}40`,
  },

  '& .rfl-list': {
    display: 'flex',
    flexDirection: 'column',
  },
  '& .rfl-empty': {
    padding: '18px 16px',
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  '& .rfl-row': {
    display: 'grid',
    gridTemplateColumns: '14px minmax(0, 1fr) auto',
    columnGap: '12px',
    alignItems: 'start',
    padding: '10px 16px',
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textBody,
  },
  '& .rfl-row:last-child': { borderBottom: 'none' },
  '& .rfl-row.tier-green': { background: `${theme.colors.statusOnTrackBg}80` },
  '& .rfl-row.tier-yellow': { background: `${theme.colors.statusBehindBg}99` },
  '& .rfl-row.tier-red': { background: `${theme.colors.statusOffTrackBg}99` },

  '& .rfl-row-dot': {
    width: '10px',
    height: '10px',
    borderRadius: theme.radii.circle,
    marginTop: '4px',
  },
  '& .rfl-row.tier-green  .rfl-row-dot': {
    background: theme.colors.statusOnTrackSoft,
  },
  '& .rfl-row.tier-yellow .rfl-row-dot': {
    background: theme.colors.statusBehindSoft,
  },
  '& .rfl-row.tier-red    .rfl-row-dot': {
    background: theme.colors.statusOffTrackSoft,
  },

  '& .rfl-row-body': {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  '& .rfl-row-headline': {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    flexWrap: 'wrap',
  },
  '& .rfl-row-route': {
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
  },
  '& .rfl-row-sep': { color: theme.colors.textHint },
  '& .rfl-row-stop': {
    color: theme.colors.textBody,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .rfl-row-meta': {
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textMuted,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center',
  },
  '& .rfl-row-eta': {
    fontVariantNumeric: 'tabular-nums',
  },
  '& .rfl-row-eta .original': {
    color: theme.colors.textMuted,
    textDecoration: 'line-through',
    textDecorationColor: theme.colors.border,
  },
  '& .rfl-row-eta .arrow': {
    color: theme.colors.textHint,
    margin: '0 4px',
  },
  '& .rfl-row-eta .projected': {
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
  },
  '& .rfl-row.tier-yellow .rfl-row-eta .projected': {
    color: theme.colors.statusBehind,
  },
  '& .rfl-row.tier-red    .rfl-row-eta .projected': {
    color: theme.colors.statusOffTrack,
  },

  '& .rfl-row-reason': {
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textMuted,
    fontStyle: 'italic',
  },
  '& .rfl-row.tier-yellow .rfl-row-reason': {
    color: theme.colors.statusBehind,
    fontStyle: 'normal',
  },
  '& .rfl-row.tier-red .rfl-row-reason': {
    color: theme.colors.statusOffTrack,
    fontStyle: 'normal',
  },
});

function flattenForecastRows(drivers, selectedRouteId) {
  const rows = [];
  drivers.forEach((driver) => {
    if (selectedRouteId && driver.routeId !== selectedRouteId) return;
    if (driver.status === 'not-started' || driver.status === 'completed')
      return;
    (driver.routeStops || []).forEach((stop) => {
      if (stop.state !== 'pending') return;
      if (!stop.riskTier) return;
      // Hide green-tier rows — the list is a "what's at risk" surface.
      if (stop.riskTier === 'green') return;
      rows.push({
        key: `${driver.routeId}-${stop.stopIndex}-${stop.stopId}`,
        routeName: driver.routeName,
        stopIndex: stop.stopIndex,
        stopName: stop.stopName,
        plannedWindow: stop.plannedWindow,
        originalEtaLabel: stop.originalEtaLabel,
        currentEtaLabel: stop.currentEtaLabel,
        riskTier: stop.riskTier,
        riskReason: stop.riskReason,
        currentEtaMs: stop.currentEtaMs,
      });
    });
  });
  rows.sort((a, b) => {
    const tierDiff =
      (TIER_ORDER[a.riskTier] ?? 9) - (TIER_ORDER[b.riskTier] ?? 9);
    if (tierDiff !== 0) return tierDiff;
    return (a.currentEtaMs || 0) - (b.currentEtaMs || 0);
  });
  return rows;
}

function RouteForecastList({ drivers, selectedRouteId }) {
  const rows = useMemo(
    () => flattenForecastRows(drivers, selectedRouteId),
    [drivers, selectedRouteId],
  );
  const atRiskCount = rows.length;

  return (
    <StyledDiv data-testid="route-forecast-list">
      <div className="rfl-header">
        <div>
          <span className="rfl-title">ETA risk · upcoming stops</span>
          <span className="rfl-title-meta">
            Original → projected ETA and forecast tier (carry-forward delay)
          </span>
        </div>
        <span
          className={`rfl-summary-chip ${atRiskCount === 0 ? 'zero' : ''}`}
          data-testid="route-forecast-chip"
        >
          <strong>{atRiskCount}</strong>
          {' at risk'}
        </span>
      </div>

      {atRiskCount === 0 ? (
        <div className="rfl-empty" data-testid="route-forecast-empty">
          All upcoming stops are on track.
        </div>
      ) : (
        <div className="rfl-list">
          {rows.map((row) => (
            <div
              key={row.key}
              className={`rfl-row tier-${row.riskTier}`}
              data-testid={`route-forecast-row-${row.key}`}
            >
              <span className="rfl-row-dot" aria-hidden="true" />
              <div className="rfl-row-body">
                <div className="rfl-row-headline">
                  <span className="rfl-row-route">{row.routeName}</span>
                  <span className="rfl-row-sep">·</span>
                  <span className="rfl-row-stop">
                    {`Stop ${row.stopIndex} — ${row.stopName}`}
                  </span>
                </div>
                <div className="rfl-row-meta">
                  <span>{`Window ${row.plannedWindow}`}</span>
                  <span className="rfl-row-eta">
                    <span className="original">{row.originalEtaLabel}</span>
                    <span className="arrow" aria-hidden="true">
                      →
                    </span>
                    <span className="projected">{row.currentEtaLabel}</span>
                  </span>
                </div>
                {row.riskReason && (
                  <div className="rfl-row-reason">{row.riskReason}</div>
                )}
              </div>
              {/* Building-closure flag returns when the BE closure query
                  lands (stop.closesAtMs). */}
            </div>
          ))}
        </div>
      )}
    </StyledDiv>
  );
}

RouteForecastList.propTypes = {
  drivers: PropTypes.arrayOf(driverRecordShape).isRequired,
  selectedRouteId: PropTypes.string,
};

RouteForecastList.defaultProps = {
  selectedRouteId: null,
};

export default RouteForecastList;
